/**
 * Module de sécurité centralisé pour Wanzo Land
 * 
 * @description Protection contre les principales vulnérabilités web:
 * - XSS (Cross-Site Scripting)
 * - CSRF (Cross-Site Request Forgery)
 * - Open Redirect
 * - Path Traversal
 * - Parameter Tampering
 * - Rate Limiting (côté client)
 * 
 * @since 2026-01-13
 */

// ============================================================================
// VALIDATION DES URLs ET REDIRECTIONS (Open Redirect Protection)
// ============================================================================

/**
 * Liste blanche des domaines autorisés pour les redirections
 */
const ALLOWED_REDIRECT_DOMAINS = [
  'wanzzo.com',
  'www.wanzzo.com',
  'api.wanzzo.com',
  'wanzo.land',
  'www.wanzo.land',
  'api.wanzo.land',
  'api.wanzo-land.com',
  'localhost',
  '127.0.0.1',
];

/**
 * Valide si une URL de redirection est sûre (prévention Open Redirect)
 * @param url - URL à valider
 * @returns true si l'URL est sûre pour la redirection
 */
export const isValidRedirectUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  
  const trimmedUrl = url.trim();
  
  // Autoriser les chemins relatifs commençant par /
  if (trimmedUrl.startsWith('/') && !trimmedUrl.startsWith('//')) {
    // Vérifier qu'il n'y a pas de caractères dangereux
    if (!/[<>"'`]/.test(trimmedUrl)) {
      return true;
    }
    return false;
  }
  
  // Vérifier les URLs absolues
  try {
    const parsed = new URL(trimmedUrl);
    
    // Rejeter les protocoles dangereux
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      console.warn('🚨 Protocole non autorisé:', parsed.protocol);
      return false;
    }
    
    // Vérifier si le domaine est dans la liste blanche
    const hostname = parsed.hostname.toLowerCase();
    const isAllowed = ALLOWED_REDIRECT_DOMAINS.some(domain => 
      hostname === domain || hostname.endsWith(`.${domain}`)
    );
    
    if (!isAllowed) {
      console.warn('🚨 Domaine non autorisé pour redirection:', hostname);
    }
    
    return isAllowed;
  } catch {
    return false;
  }
};

/**
 * Sanitise une URL de redirection - retourne '/' si non sûre
 */
export const sanitizeRedirectUrl = (url: string): string => {
  if (isValidRedirectUrl(url)) {
    return url.trim();
  }
  console.warn('🚨 URL de redirection non sûre, redirection vers /', url);
  return '/';
};

// ============================================================================
// VALIDATION DES PARAMÈTRES D'URL ET IDs (Path Traversal & Parameter Tampering)
// ============================================================================

/**
 * Pattern pour valider les UUIDs
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Pattern pour les IDs Auth0
 */
const AUTH0_ID_PATTERN = /^(auth0\|)?[a-zA-Z0-9_-]{10,64}$/;

/**
 * Pattern pour les IDs génériques alphanumériques
 */
const ALPHANUMERIC_ID_PATTERN = /^[a-zA-Z0-9_-]{1,128}$/;

/**
 * Valide un ID (UUID, Auth0, ou alphanumérique)
 * @param id - ID à valider
 * @param type - Type d'ID attendu
 * @returns true si l'ID est valide
 */
export const isValidId = (
  id: string | undefined | null, 
  type: 'uuid' | 'auth0' | 'alphanumeric' = 'alphanumeric'
): boolean => {
  if (!id || typeof id !== 'string') return false;
  
  const trimmedId = id.trim();
  
  switch (type) {
    case 'uuid':
      return UUID_PATTERN.test(trimmedId);
    case 'auth0':
      return AUTH0_ID_PATTERN.test(trimmedId);
    case 'alphanumeric':
    default:
      return ALPHANUMERIC_ID_PATTERN.test(trimmedId);
  }
};

/**
 * Sanitise un ID - retourne une chaîne vide si invalide
 */
export const sanitizeId = (
  id: string | undefined | null,
  type: 'uuid' | 'auth0' | 'alphanumeric' = 'alphanumeric'
): string => {
  if (!isValidId(id, type)) {
    console.warn(`🚨 ID invalide (type: ${type}):`, id);
    return '';
  }
  return id!.trim();
};

/**
 * Valide un paramètre d'URL contre les attaques de Path Traversal
 */
export const isPathSafe = (param: string): boolean => {
  if (!param || typeof param !== 'string') return false;
  
  // Détecter les tentatives de traversée de chemin
  const dangerousPatterns = [
    /\.\./,           // Traversée parent
    /%2e%2e/i,        // Encodé URL
    /\\/,             // Backslash Windows
    /%5c/i,           // Backslash encodé
    /%00/,            // Null byte
    /\0/,             // Null caractère
  ];
  
  for (const pattern of dangerousPatterns) {
    if (pattern.test(param)) {
      console.warn('🚨 Tentative de Path Traversal détectée:', param);
      return false;
    }
  }
  
  return true;
};

// ============================================================================
// PROTECTION CSRF (Cross-Site Request Forgery)
// ============================================================================

/**
 * Génère un token CSRF
 */
export const generateCsrfToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Stocke le token CSRF dans sessionStorage
 */
export const setCsrfToken = (): string => {
  const token = generateCsrfToken();
  sessionStorage.setItem('csrf_token', token);
  return token;
};

/**
 * Récupère le token CSRF depuis sessionStorage
 */
export const getCsrfToken = (): string | null => {
  return sessionStorage.getItem('csrf_token');
};

/**
 * Valide un token CSRF
 */
export const validateCsrfToken = (token: string): boolean => {
  const storedToken = getCsrfToken();
  if (!storedToken || !token) return false;
  
  // Comparaison en temps constant pour éviter les timing attacks
  if (storedToken.length !== token.length) return false;
  
  let result = 0;
  for (let i = 0; i < storedToken.length; i++) {
    result |= storedToken.charCodeAt(i) ^ token.charCodeAt(i);
  }
  
  return result === 0;
};

// ============================================================================
// RATE LIMITING (Côté Client)
// ============================================================================

interface RateLimitEntry {
  count: number;
  firstRequest: number;
  lastRequest: number;
}

const rateLimitStore: Map<string, RateLimitEntry> = new Map();

/**
 * Configuration du rate limiting par action
 */
const RATE_LIMIT_CONFIG = {
  // Actions critiques
  login: { maxRequests: 5, windowMs: 60 * 1000 },       // 5 requêtes/minute
  signup: { maxRequests: 3, windowMs: 60 * 1000 },     // 3 requêtes/minute
  passwordReset: { maxRequests: 3, windowMs: 60 * 1000 }, // 3 requêtes/minute
  
  // Actions sensibles
  apiCall: { maxRequests: 100, windowMs: 60 * 1000 },  // 100 requêtes/minute
  formSubmit: { maxRequests: 10, windowMs: 60 * 1000 }, // 10 requêtes/minute
  
  // Actions générales
  default: { maxRequests: 200, windowMs: 60 * 1000 },  // 200 requêtes/minute
};

/**
 * Vérifie si une action dépasse le rate limit
 * @param action - Type d'action
 * @param identifier - Identifiant unique (userId, IP simulée, etc.)
 * @returns true si l'action est autorisée, false si rate limit dépassé
 */
export const checkRateLimit = (
  action: keyof typeof RATE_LIMIT_CONFIG | string,
  identifier: string = 'anonymous'
): { allowed: boolean; retryAfter?: number } => {
  const config = RATE_LIMIT_CONFIG[action as keyof typeof RATE_LIMIT_CONFIG] 
    || RATE_LIMIT_CONFIG.default;
  
  const key = `${action}:${identifier}`;
  const now = Date.now();
  
  const entry = rateLimitStore.get(key);
  
  if (!entry) {
    // Première requête
    rateLimitStore.set(key, {
      count: 1,
      firstRequest: now,
      lastRequest: now,
    });
    return { allowed: true };
  }
  
  // Vérifier si la fenêtre est expirée
  if (now - entry.firstRequest > config.windowMs) {
    // Reset la fenêtre
    rateLimitStore.set(key, {
      count: 1,
      firstRequest: now,
      lastRequest: now,
    });
    return { allowed: true };
  }
  
  // Vérifier si le limit est atteint
  if (entry.count >= config.maxRequests) {
    const retryAfter = Math.ceil((entry.firstRequest + config.windowMs - now) / 1000);
    console.warn(`🚨 Rate limit dépassé pour ${action}. Réessayer dans ${retryAfter}s`);
    return { 
      allowed: false, 
      retryAfter 
    };
  }
  
  // Incrémenter le compteur
  rateLimitStore.set(key, {
    ...entry,
    count: entry.count + 1,
    lastRequest: now,
  });
  
  return { allowed: true };
};

/**
 * Nettoie les entrées expirées du rate limiter
 */
export const cleanupRateLimitStore = (): void => {
  const now = Date.now();
  const maxAge = 5 * 60 * 1000; // 5 minutes
  
  for (const [key, entry] of rateLimitStore.entries()) {
    if (now - entry.lastRequest > maxAge) {
      rateLimitStore.delete(key);
    }
  }
};

// Nettoyage automatique toutes les 5 minutes
if (typeof window !== 'undefined') {
  setInterval(cleanupRateLimitStore, 5 * 60 * 1000);
}

// ============================================================================
// SECURE STORAGE (Protection localStorage/sessionStorage)
// ============================================================================

/**
 * Données sensibles qui ne doivent JAMAIS être loggées
 */
const SENSITIVE_KEYS = [
  'password',
  'token',
  'secret',
  'apiKey',
  'api_key',
  'refresh_token',
  'access_token',
  'auth',
  'credentials',
  'credit_card',
  'cvv',
  'ssn',
  'pin',
];

/**
 * Vérifie si une clé représente des données sensibles
 */
export const isSensitiveKey = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYS.some(sensitive => lowerKey.includes(sensitive));
};

/**
 * Masque les données sensibles pour le logging
 */
export const maskSensitiveData = (data: unknown): unknown => {
  // Valeur scalaire (ou corps absent) : rien à masquer.
  if (data === null || typeof data !== 'object') return data;
  const masked: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (isSensitiveKey(key)) {
      masked[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      masked[key] = maskSensitiveData(value);
    } else {
      masked[key] = value;
    }
  }
  
  return masked;
};

/**
 * Wrapper sécurisé pour localStorage
 */
export const secureStorage = {
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error('Erreur localStorage:', e);
    }
  },
  
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.error('Erreur localStorage:', e);
      return null;
    }
  },
  
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Erreur localStorage:', e);
    }
  },
  
  /**
   * Efface toutes les données d'authentification (logout sécurisé)
   */
  clearAuthData: (): void => {
    const authKeys = [
      'auth0_token',
      'auth0_id_token',
      'auth0_refresh_token',
      'auth0_expires_in',
      'auth0_token_type',
      'auth0_user',
      'auth0_state',
      'auth0_code_verifier',
      'token_refresh_in_progress',
    ];
    
    authKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      } catch (e) {
        // Ignorer les erreurs
      }
    });
  },
};

// ============================================================================
// INPUT VALIDATION HELPERS
// ============================================================================

/**
 * Valide un numéro de téléphone
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  if (!phone || typeof phone !== 'string') return false;
  
  // Pattern international flexible
  const phonePattern = /^\+?[\d\s\-()]{8,20}$/;
  return phonePattern.test(phone.trim());
};

/**
 * Valide un email avec protection contre les caractères dangereux
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  
  const trimmed = email.trim().toLowerCase();
  
  // Vérifier la longueur
  if (trimmed.length > 254) return false;
  
  // Vérifier les caractères dangereux
  if (/[<>"'`\\]/.test(trimmed)) return false;
  
  // Pattern email standard
  const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  return emailPattern.test(trimmed);
};

/**
 * Valide un montant numérique
 */
export const isValidAmount = (amount: string | number): boolean => {
  if (typeof amount === 'number') {
    return !isNaN(amount) && isFinite(amount) && amount >= 0;
  }
  
  if (typeof amount === 'string') {
    const parsed = parseFloat(amount.replace(/[,\s]/g, ''));
    return !isNaN(parsed) && isFinite(parsed) && parsed >= 0;
  }
  
  return false;
};

// ============================================================================
// CONTENT SECURITY
// ============================================================================

/**
 * Vérifie si le contenu contient des scripts potentiellement malveillants
 */
export const containsMaliciousContent = (content: string): boolean => {
  if (!content || typeof content !== 'string') return false;
  
  const maliciousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript\s*:/gi,
    /on\w+\s*=/gi,
    /data\s*:\s*text\/html/gi,
    /<iframe/gi,
    /<object/gi,
    /<embed/gi,
    /<link.*?href/gi,
    /<meta.*?http-equiv/gi,
    /expression\s*\(/gi,
    /url\s*\(\s*["']?\s*javascript:/gi,
  ];
  
  for (const pattern of maliciousPatterns) {
    if (pattern.test(content)) {
      console.warn('🚨 Contenu malveillant détecté');
      return true;
    }
  }
  
  return false;
};

// ============================================================================
// SECURE FETCH WRAPPER
// ============================================================================

/**
 * Headers de sécurité à ajouter aux requêtes
 */
export const getSecurityHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  
  // Ajouter le token CSRF si disponible
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }
  
  // Indiquer que la requête vient de l'application
  headers['X-Requested-With'] = 'XMLHttpRequest';
  
  return headers;
};

// ============================================================================
// EXPORTS PAR DÉFAUT
// ============================================================================

export default {
  // URL & Redirect Security
  isValidRedirectUrl,
  sanitizeRedirectUrl,
  
  // ID Validation
  isValidId,
  sanitizeId,
  isPathSafe,
  
  // CSRF Protection
  generateCsrfToken,
  setCsrfToken,
  getCsrfToken,
  validateCsrfToken,
  
  // Rate Limiting
  checkRateLimit,
  cleanupRateLimitStore,
  
  // Secure Storage
  secureStorage,
  isSensitiveKey,
  maskSensitiveData,
  
  // Input Validation
  isValidPhoneNumber,
  isValidEmail,
  isValidAmount,
  
  // Content Security
  containsMaliciousContent,
  getSecurityHeaders,
};
